import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { Table, Button, Container, Row, Col, Card, Badge, Modal, Accordion, Form, InputGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaEnvelope, FaPhone, FaWhatsapp } from 'react-icons/fa';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ManageallAgency.css';
import BaseUrl from '../../ApiServices/BaseUrl';

function ManageallAgency() {
  const [agencies, setAgencies] = useState([]);
  const [filteredAgencies, setFilteredAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agencyCounts, setAgencyCounts] = useState({
    total: 0,
    verified: 0,
    pending: 0
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelAgency, setLabelAgency] = useState(null);
  const [labelInput, setLabelInput] = useState('');
  const navigate = useNavigate();

  const tableRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - tableRef.current.offsetLeft;
    scrollLeft.current = tableRef.current.scrollLeft;
    tableRef.current.style.cursor = 'grabbing';
  };
  const onMouseLeave = () => { isDragging.current = false; tableRef.current.style.cursor = 'grab'; };
  const onMouseUp = () => { isDragging.current = false; tableRef.current.style.cursor = 'grab'; };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - tableRef.current.offsetLeft;
    tableRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };

  useEffect(() => {
    fetchAgencies();
  }, []);

  useEffect(() => {
    filterAgencies();
  }, [searchTerm, agencies]);

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${BaseUrl}/api/agency`);
      const agenciesData = Array.isArray(response.data) ? response.data : [];

      if (!Array.isArray(agenciesData)) {
        console.warn("No agency records found");
        setAgencies([]);
        setFilteredAgencies([]);
        setLoading(false);
        return;
      }

      const total = agenciesData.length;
      const verified = agenciesData.filter(agency =>
        agency.is_verified === 1
      ).length;
      const pending = agenciesData.filter(agency =>
        agency.is_verified === 0 || !agency.is_verified
      ).length;

      setAgencyCounts({ total, verified, pending });
      
      // Sort by latest date (created_at)
      const sortedData = agenciesData.sort((a, b) => {
        const aDate = new Date(a.created_at || 0);
        const bDate = new Date(b.created_at || 0);
        return bDate - aDate; // Descending order (latest first)
      });
      
      setAgencies(sortedData);
      setFilteredAgencies(sortedData);
      setLoading(false);
    } catch (err) {
      console.error("❌ Error fetching agencies:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const filterAgencies = () => {
    if (!searchTerm.trim()) {
      setFilteredAgencies(agencies);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = agencies.filter(agency => {
      return (
        (agency.agency_name && agency.agency_name.toLowerCase().includes(searchLower)) ||
        (agency.email && agency.email.toLowerCase().includes(searchLower)) ||
        (agency.owner_name && agency.owner_name.toLowerCase().includes(searchLower)) ||
        (agency.manager_name && agency.manager_name.toLowerCase().includes(searchLower)) ||
        (agency.city && agency.city.toLowerCase().includes(searchLower)) ||
        (agency.country && agency.country.toLowerCase().includes(searchLower)) ||
        (agency.official_phone && agency.official_phone.includes(searchTerm)) ||
        (agency.whatsapp_number_1 && agency.whatsapp_number_1.includes(searchTerm)) ||
        (agency.owner_mobile && agency.owner_mobile.includes(searchTerm)) ||
        (agency.manager_mobile && agency.manager_mobile.includes(searchTerm))
      );
    });

    setFilteredAgencies(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleViewDetails = (agency) => {
    setSelectedAgency(agency);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedAgency(null);
  };

  const handleOpenLabelModal = (agency) => {
    setLabelAgency(agency);
    setLabelInput(agency.custom_label || '');
    setShowLabelModal(true);
  };

  const handleCloseLabelModal = () => {
    setShowLabelModal(false);
    setLabelAgency(null);
    setLabelInput('');
  };

  const handleSaveLabel = async () => {
    if (!labelAgency) return;
    const trimmed = labelInput.trim();
    try {
      await axios.put(`${BaseUrl}/api/agency/${labelAgency.id}/custom-label`, {
        custom_label: trimmed || null
      });
      const updatedAgencies = agencies.map(item =>
        item.id === labelAgency.id ? { ...item, custom_label: trimmed || null } : item
      );
      setAgencies(updatedAgencies);
      setFilteredAgencies(updatedAgencies);
      handleCloseLabelModal();
      Swal.fire('Success!', 'Custom label has been saved.', 'success');
    } catch (err) {
      Swal.fire('Error!', err.response?.data?.error || 'Failed to save custom label', 'error');
    }
  };

  const handleRemoveLabel = async () => {
    if (!labelAgency) return;
    try {
      await axios.put(`${BaseUrl}/api/agency/${labelAgency.id}/custom-label`, {
        custom_label: null
      });
      const updatedAgencies = agencies.map(item =>
        item.id === labelAgency.id ? { ...item, custom_label: null } : item
      );
      setAgencies(updatedAgencies);
      setFilteredAgencies(updatedAgencies);
      handleCloseLabelModal();
      Swal.fire('Removed!', 'Custom label has been removed.', 'success');
    } catch (err) {
      Swal.fire('Error!', err.response?.data?.error || 'Failed to remove custom label', 'error');
    }
  };

  const confirmDelete = (agency) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete agency "${agency.agency_name}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${BaseUrl}/api/agency/${agency.id}`);
          setAgencies(agencies.filter(item => item.id !== agency.id));
          setFilteredAgencies(filteredAgencies.filter(item => item.id !== agency.id));
          fetchAgencies();

          Swal.fire(
            'Deleted!',
            'The agency has been deleted successfully.',
            'success'
          );
        } catch (err) {
          Swal.fire(
            'Error!',
            err.response?.data?.error || 'Failed to delete agency',
            'error'
          );
        }
      }
    });
  };

  const toggleVerificationStatus = async (agency) => {
    try {
      const newStatus = agency.is_verified ? 0 : 1;
      const response = await axios.put(`${BaseUrl}/api/agency/${agency.id}`, {
        is_verified: newStatus
      });

      // Update local state
      const updatedAgencies = agencies.map(item =>
        item.id === agency.id ? { ...item, is_verified: newStatus } : item
      );

      setAgencies(updatedAgencies);
      setFilteredAgencies(updatedAgencies);

      // Send email notification when agency is verified (status changed to 1)
      if (newStatus === 1) {
        try {
          await axios.post(`${BaseUrl}/api/send-agency-verification-email`, {
            to: agency.email,
            firstName: agency.owner_name || agency.contact_person_name || agency.agency_name,
            agencyName: agency.agency_name
          });
          console.log('Verification email sent successfully to:', agency.email);
        } catch (emailError) {
          console.error('Failed to send verification email:', emailError);
          // Don't show error to admin - just log it
        }
      }

      // Build success message with auto-verification details
      let successMsg = `Agency has been ${newStatus ? 'verified' : 'unverified'}.`;
      if (newStatus === 1 && response.data?.autoVerified) {
        const { candidates, jobs } = response.data.autoVerified;
        if (candidates > 0 || jobs > 0) {
          successMsg += ` Auto-verified ${candidates} candidate(s) and ${jobs} job(s).`;
        }
        successMsg += ' Verification email sent to agency.';
      }
      if (newStatus === 0 && response.data?.autoUnverified) {
        const { candidates, jobs } = response.data.autoUnverified;
        if (candidates > 0 || jobs > 0) {
          successMsg += ` Auto-unverified ${candidates} candidate(s) and ${jobs} job(s).`;
        }
      }

      Swal.fire(
        'Success!',
        successMsg,
        'success'
      );
    } catch (err) {
      Swal.fire(
        'Error!',
        err.response?.data?.error || 'Failed to update verification status',
        'error'
      );
    }
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ?
      <Badge className="manage-all-agency-badge-verified" bg="success">Verified</Badge> :
      <Badge className="manage-all-agency-badge-pending" bg="warning">Pending</Badge>;
  };

  const getSubscriptionBadge = (subscriptionStatus) => {
    if (!subscriptionStatus) return <Badge className="manage-all-agency-badge-inactive" bg="secondary">No Plan</Badge>;

    switch (subscriptionStatus.toLowerCase()) {
      case 'active':
      case 'paid':
        return <Badge className="manage-all-agency-badge-active" bg="success">Active</Badge>;
      case 'inactive':
        return <Badge className="manage-all-agency-badge-inactive" bg="secondary">Inactive</Badge>;
      case 'pending':
        return <Badge className="manage-all-agency-badge-pending" bg="warning">Pending</Badge>;
      case 'expired':
        return <Badge className="manage-all-agency-badge-expired" bg="danger">Expired</Badge>;
      default:
        return <Badge className="manage-all-agency-badge-unknown" bg="info">{subscriptionStatus}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const getCandidatePostingStatus = (candidatePosting) => {
    return candidatePosting === 1 ?
      <Badge className="manage-all-agency-badge-active" bg="success">Enabled</Badge> :
      <Badge className="manage-all-agency-badge-inactive" bg="secondary">Disabled</Badge>;
  };

  // Function to get all contact numbers
  const getContactNumbers = (agency) => {
    const contacts = [];

    if (agency.official_phone) {
      contacts.push({
        type: 'Official Phone',
        number: agency.official_phone,
        icon: <FaPhone className="text-secondary" />
      });
    }

    if (agency.whatsapp_number_1) {
      contacts.push({
        type: 'WhatsApp 1',
        number: agency.whatsapp_number_1,
        icon: <FaWhatsapp className="text-success" />
      });
    }

    if (agency.whatsapp_number_2) {
      contacts.push({
        type: 'WhatsApp 2',
        number: agency.whatsapp_number_2,
        icon: <FaWhatsapp className="text-success" />
      });
    }

    if (agency.whatsapp_number_3) {
      contacts.push({
        type: 'WhatsApp 3',
        number: agency.whatsapp_number_3,
        icon: <FaWhatsapp className="text-success" />
      });
    }

    if (agency.whatsapp_number_4) {
      contacts.push({
        type: 'WhatsApp 4',
        number: agency.whatsapp_number_4,
        icon: <FaWhatsapp className="text-success" />
      });
    }

    if (agency.owner_mobile) {
      contacts.push({
        type: 'Owner Mobile',
        number: agency.owner_mobile,
        icon: <FaPhone className="text-info" />
      });
    }

    if (agency.manager_mobile) {
      contacts.push({
        type: 'Manager Mobile',
        number: agency.manager_mobile,
        icon: <FaPhone className="text-warning" />
      });
    }

    return contacts;
  };

  // Email Popover Component
  const EmailPopover = (email) => (
    <Popover id="email-popover">
      <Popover.Header as="h3">Email Address</Popover.Header>
      <Popover.Body>
        <div className="d-flex align-items-center">
          <FaEnvelope className="me-2 text-primary" />
          <a href={`mailto:${email}`} className="text-decoration-none">{email}</a>
        </div>
      </Popover.Body>
    </Popover>
  );

  // Phone Popover Component
  const PhonePopover = (contacts) => (
    <Popover id="phone-popover" style={{ maxWidth: '300px' }}>
      <Popover.Header as="h3">Contact Numbers</Popover.Header>
      <Popover.Body>
        {contacts.map((contact, idx) => (
          <div key={idx} className="d-flex align-items-center mb-2 pb-2 border-bottom">
            <span className="me-2">{contact.icon}</span>
            <div>
              <small className="text-muted d-block">{contact.type}</small>
              <a
                href={`tel:${contact.number}`}
                className="text-decoration-none"
                style={{ fontSize: '0.9rem' }}
              >
                {contact.number}
              </a>
            </div>
          </div>
        ))}
        {contacts.length === 0 && (
          <div className="text-muted">No contact numbers available</div>
        )}
      </Popover.Body>
    </Popover>
  );

  if (loading) return (
    <>
      <AdminNavbar />
      <div className="manage-all-agency-loading text-center mt-5" style={{ marginTop: '100px' }}>Loading agencies...</div>
    </>
  );

  if (error) return (
    <>
      <AdminNavbar />
      <div className="manage-all-agency-error text-center mt-5 text-danger" style={{ marginTop: '100px' }}>Error: {error}</div>
    </>
  );

  return (
    <>
      <AdminNavbar />
      <Container className="manage-all-agency-container" style={{ marginTop: '80px' }}>
        <Row className="mb-4 manage-all-agency-text">
          <Col style={{ marginTop: '20px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2>Manage Agencies</h2>
                <p className="text-muted">View and manage all recruitment agencies</p>
              </div>
              <div className="manage-all-agency-search-bar-container" style={{ minWidth: '300px' }}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search agencies..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="manage-all-agency-search-input"
                  />
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                </InputGroup>
                {searchTerm && (
                  <div className="text-muted small mt-1">
                    Showing {filteredAgencies.length} of {agencies.length} agencies
                  </div>
                )}
              </div>
            </div>

            {/* Agency Counts */}
            <Row className="mb-4 gx-2">
              <Col xs={4} md={4}>
                <Card className="text-center bg-primary text-white">
                  <Card.Body className="p-2">
                    <h3 className="mb-1">{agencyCounts.total}</h3>
                    <p className="mb-0 small">Total</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={4} md={4}>
                <Card className="text-center bg-success text-white">
                  <Card.Body className="p-2">
                    <h3 className="mb-1">{agencyCounts.verified}</h3>
                    <p className="mb-0 small">Verified</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={4} md={4}>
                <Card className="text-center bg-warning text-dark">
                  <Card.Body className="p-2">
                    <h3 className="mb-1">{agencyCounts.pending}</h3>
                    <p className="mb-0 small">Pending</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <div className='manage-all-agency-head'>
          <Card>
            <Card.Body>
              <div className="manage-all-agency-content">
                <div
                  ref={tableRef}
                  className="manage-all-agency-table-responsive"
                  onMouseDown={onMouseDown}
                  onMouseLeave={onMouseLeave}
                  onMouseUp={onMouseUp}
                  onMouseMove={onMouseMove}
                  style={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem',
                    cursor: 'grab',
                    userSelect: 'none'
                  }}>
                  <Table striped bordered hover className="manage-all-agency-table" style={{
                    minWidth: '1900px',
                    width: '100%',
                    marginBottom: 0
                  }}>
                    <thead className="manage-all-agency-table-header table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ minWidth: '80px' }}>S.No</th>
                        <th style={{ minWidth: '200px' }}>Agency Name</th>
                        <th style={{ minWidth: '60px', textAlign: 'center' }}>Email</th>
                        <th style={{ minWidth: '60px', textAlign: 'center' }}>Phone</th>
                        <th style={{ minWidth: '150px' }}>City/Country</th>
                        <th style={{ minWidth: '150px' }}>Owner Name</th>
                        <th style={{ minWidth: '120px' }}>Verification</th>
                        <th style={{ minWidth: '120px' }}>Subscription</th>
                        <th style={{ minWidth: '120px' }}>Candidate Posting</th>
                        <th style={{ minWidth: '120px' }}>Registered Date</th>
                        <th style={{ minWidth: '350px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="manage-all-agency-table-body">
                      {filteredAgencies.map((agency, index) => {
                        const email = agency.email || 'N/A';
                        const contactNumbers = getContactNumbers(agency);

                        return (
                          <tr key={agency.id} className="manage-all-agency-table-row">
                            <td>{index + 1}</td>
                            <td className="manage-all-agency-agency-name fw-semibold">
                              {agency.agency_name || 'N/A'}
                            </td>
                            <td className="text-center">
                              {email !== 'N/A' ? (
                                <OverlayTrigger
                                  trigger={['click', 'focus']}
                                  placement="left"
                                  overlay={EmailPopover(email)}
                                  rootClose
                                >
                                  <button
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: '6px',
                                      cursor: 'pointer',
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s',
                                      color: '#0d6efd'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    title="Click to view email"
                                  >
                                    <FaEnvelope size={18} />
                                  </button>
                                </OverlayTrigger>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="text-center">
                              {contactNumbers.length > 0 ? (
                                <OverlayTrigger
                                  trigger={['click', 'focus']}
                                  placement="left"
                                  overlay={PhonePopover(contactNumbers)}
                                  rootClose
                                >
                                  <button
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: '6px',
                                      cursor: 'pointer',
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s',
                                      color: '#28a745'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    title="Click to view contact numbers"
                                  >
                                    <FaPhone size={16} />
                                  </button>
                                </OverlayTrigger>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="manage-all-agency-location">
                              {agency.city || 'N/A'} / {agency.country || 'N/A'}
                            </td>
                            <td className="manage-all-agency-contacts">
                              {agency.owner_name || agency.contact_person_name || 'N/A'}
                            </td>
                            <td className="manage-all-agency-verification">
                              {getVerificationBadge(agency.is_verified)}
                            </td>
                            <td className="manage-all-agency-subscription">
                              {getSubscriptionBadge(agency.payment_status)}
                            </td>
                            <td className="manage-all-agency-candidate-posting">
                              {getCandidatePostingStatus(agency.candidate_posting)}
                            </td>
                            <td className="manage-all-agency-registered-date">
                              {formatDate(agency.created_at)}
                            </td>
                            <td className="manage-all-agency-actions">
                              <div className="d-flex gap-2 flex-nowrap">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => navigate(`/a-myaccount?adminEditId=${agency.id}`)}
                                  className="manage-all-agency-edit-btn"
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  ✏️ Edit
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleViewDetails(agency)}
                                  className="manage-all-agency-view-btn"
                                  style={{ whiteSpace: 'nowrap', color: '#031316' }}
                                >
                                  View Details
                                </Button>
                                <Button
                                  variant={agency.is_verified ? "outline-warning" : "outline-success"}
                                  size="sm"
                                  onClick={() => toggleVerificationStatus(agency)}
                                  className="manage-all-agency-verify-btn"

                                >
                                  {agency.is_verified ? 'Unverify' : 'Verify'}
                                </Button>
                                <Button
                                  variant={agency.custom_label ? "outline-secondary" : "outline-dark"}
                                  size="sm"
                                  onClick={() => handleOpenLabelModal(agency)}
                                  className="manage-all-agency-label-btn"
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  {agency.custom_label ? 'Edit Label' : 'Assign Text'}
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => confirmDelete(agency)}
                                  className="manage-all-agency-delete-btn"
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>

                  {filteredAgencies.length === 0 && (
                    <div className="manage-all-agency-empty text-center py-4" style={{ minWidth: '1900px' }}>
                      {searchTerm ? (
                        <p className="text-muted">No agencies found matching "<strong>{searchTerm}</strong>"</p>
                      ) : (
                        <p className="text-muted">No agencies found.</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>

      {/* Agency Details Modal */}
      <Modal show={showDetailsModal} onHide={handleCloseDetails} size="xl" scrollable className="manage-all-agency-modal">
        <Modal.Header closeButton className="manage-all-agency-modal-header">
          <Modal.Title>Agency Details - {selectedAgency?.agency_name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="manage-all-agency-modal-body">
          {selectedAgency && (
            <div className="manage-all-agency-details-card">
              <Card className="mb-4 manage-all-agency-details-section">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">Agency/Business Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Agency Name:</strong> {selectedAgency.agency_name || 'N/A'}</p>
                      <p><strong>Email:</strong> {selectedAgency.email || 'N/A'}</p>
                      <p><strong>Website:</strong> {selectedAgency.website_url ?
                        <a href={selectedAgency.website_url} target="_blank" rel="noopener noreferrer">
                          {selectedAgency.website_url}
                        </a> : 'N/A'
                      }</p>
                      <p><strong>Official Phone:</strong> {selectedAgency.official_phone || 'N/A'}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>City:</strong> {selectedAgency.city || 'N/A'}</p>
                      <p><strong>Province:</strong> {selectedAgency.province || 'N/A'}</p>
                      <p><strong>Country:</strong> {selectedAgency.country || 'N/A'}</p>
                      <p><strong>Verification Status:</strong> {getVerificationBadge(selectedAgency.is_verified)}</p>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      <p><strong>Full Address:</strong> {selectedAgency.full_address || 'N/A'}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-4 manage-all-agency-details-section">
                <Card.Header className="bg-success text-white">
                  <h5 className="mb-0">Contact Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Primary WhatsApp:</strong> {selectedAgency.whatsapp_number_1 || 'N/A'}</p>
                      <p><strong>Secondary WhatsApp:</strong> {selectedAgency.whatsapp_number_2 || 'N/A'}</p>
                      <p><strong>Additional WhatsApp 3:</strong> {selectedAgency.whatsapp_number_3 || 'N/A'}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Additional WhatsApp 4:</strong> {selectedAgency.whatsapp_number_4 || 'N/A'}</p>
                      <p><strong>Role:</strong> {selectedAgency.role || 'N/A'}</p>
                      <p><strong>Last Login:</strong> {formatDate(selectedAgency.last_login_date)}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-4 manage-all-agency-details-section">
                <Card.Header className="bg-info text-white">
                  <h5 className="mb-0">Owner/Director Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Owner Name:</strong> {selectedAgency.owner_name || selectedAgency.contact_person_name || 'N/A'}</p>
                      <p><strong>Owner Nationality:</strong> {selectedAgency.owner_nationality || 'N/A'}</p>
                      <p><strong>Mobile Number:</strong> {selectedAgency.owner_mobile || 'N/A'}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Owner Email:</strong> {selectedAgency.owner_email || 'N/A'}</p>
                      <p><strong>Owner Photo:</strong> {selectedAgency.owner_photo ? 'Uploaded' : 'Not Uploaded'}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-4 manage-all-agency-details-section">
                <Card.Header className="bg-warning text-dark">
                  <h5 className="mb-0">Manager Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Manager Name:</strong> {selectedAgency.manager_name || 'N/A'}</p>
                      <p><strong>Manager Nationality:</strong> {selectedAgency.manager_nationality || 'N/A'}</p>
                      <p><strong>Manager Mobile:</strong> {selectedAgency.manager_mobile || 'N/A'}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Manager Email:</strong> {selectedAgency.manager_email || 'N/A'}</p>
                      <p><strong>Manager Photo:</strong> {selectedAgency.manager_photo ? 'Uploaded' : 'Not Uploaded'}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-4 manage-all-agency-details-section">
                <Card.Header className="bg-secondary text-white">
                  <h5 className="mb-0">Subscription & Plans</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Job Posting Plan:</strong> {selectedAgency.plan_name || 'No Plan'}</p>
                      <p><strong>Plan Days:</strong> {selectedAgency.plan_days || 'N/A'}</p>
                      <p><strong>Plan Start Date:</strong> {formatDate(selectedAgency.plan_startdate)}</p>
                      <p><strong>Plan End Date:</strong> {formatDate(selectedAgency.plan_enddate)}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>Payment Status:</strong> {getSubscriptionBadge(selectedAgency.payment_status)}</p>
                      <p><strong>Payment Amount:</strong> {selectedAgency.payment_amount || 'N/A'}</p>
                      <p><strong>Candidate Posting:</strong> {getCandidatePostingStatus(selectedAgency.candidate_posting)}</p>
                      <p><strong>Candidate Contact:</strong> {selectedAgency.candidate_contact === 1 ? 'Enabled' : 'Disabled'}</p>
                    </Col>
                  </Row>
                  {selectedAgency.candidate_plan_name && (
                    <Row>
                      <Col md={12}>
                        <hr />
                        <p><strong>Candidate Plan:</strong> {selectedAgency.candidate_plan_name}</p>
                        <p><strong>Candidate Plan Days:</strong> {selectedAgency.candidate_plan_days || 'N/A'}</p>
                        <p><strong>Candidate Plan Start:</strong> {formatDate(selectedAgency.candidate_plan_startdate)}</p>
                        <p><strong>Candidate Plan End:</strong> {formatDate(selectedAgency.candidate_plan_enddate)}</p>
                      </Col>
                    </Row>
                  )}
                </Card.Body>
              </Card>

              <Card className="mb-4 manage-all-agency-details-section">
                <Card.Header className="bg-dark text-white">
                  <h5 className="mb-0" style={{ color: 'white' }}>Document Uploads</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <p><strong>Company Logo:</strong> {selectedAgency.company_logo ? 'Uploaded' : 'Not Uploaded'}</p>
                      <p><strong>Business Card:</strong> {selectedAgency.business_card ? 'Uploaded' : 'Not Uploaded'}</p>
                    </Col>
                    <Col md={6}>
                      <p><strong>License Copy:</strong> {selectedAgency.license_copy ? 'Uploaded' : 'Not Uploaded'}</p>
                      <p><strong>Created At:</strong> {formatDate(selectedAgency.created_at)}</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="manage-all-agency-modal-footer">
          <Button variant="secondary" onClick={handleCloseDetails}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Custom Label Modal */}
      <Modal show={showLabelModal} onHide={handleCloseLabelModal} centered className="manage-all-agency-label-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            {labelAgency?.custom_label ? 'Edit' : 'Assign'} Custom Label
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-2">
            Agency: <strong>{labelAgency?.agency_name}</strong>
          </p>
          <Form.Group>
            <Form.Label>Custom Text</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter custom label text..."
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              maxLength={255}
              autoFocus
            />
            <Form.Text className="text-muted">
              This text will appear below the agency's profile photo on their card.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          {labelAgency?.custom_label && (
            <Button variant="outline-danger" onClick={handleRemoveLabel} className="me-auto">
              Remove Label
            </Button>
          )}
          <Button variant="secondary" onClick={handleCloseLabelModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveLabel}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ManageallAgency;
